import type { ChartOption } from "./chart-option.js"
import { ink, surface, type ThemeMode } from "../palette.js"
import { formatValue } from "../format.js"
import type { VizModel } from "../tile-model.js"
import { STALE_OPACITY, baseOption, tooltipFor } from "./base.js"

export type BarOptions = {
  mode: ThemeMode
  model: VizModel
  horizontal?: boolean
  stacked?: boolean
  showDataLabels?: boolean
  legendPosition?: "bottom" | "right"
}

const MAX_BAR_WIDTH = 28
const FILL_GAP = 2
const CORNER = 4

const MAX_LABELLED_MARKS = 12

export function barOption({
  mode,
  model,
  horizontal = false,
  stacked = false,
  showDataLabels,
  legendPosition,
}: BarOptions): ChartOption {
  const c = ink(mode)
  const base = baseOption({ mode, model, horizontal, ...(legendPosition ? { legendPosition } : {}) })

  const marks = model.categories.length * Math.max(1, model.series.length)
  const labels = marks <= MAX_LABELLED_MARKS && (showDataLabels ?? model.presentation.directLabels === "required")

  const series = model.series.map((s) => {
    const stale = s.state === "stale"

    return {
      type: "bar" as const,
      name: s.name,
      data: s.data,
      ...(stacked ? { stack: "total" } : {}),
      barMaxWidth: MAX_BAR_WIDTH,
      ...(stacked ? {} : { barGap: "12%", barCategoryGap: "32%" }),
      itemStyle: {
        color: s.color,
        opacity: stale ? STALE_OPACITY : 1,
        borderRadius: horizontal
          ? ([0, CORNER, CORNER, 0] as [number, number, number, number])
          : ([CORNER, CORNER, 0, 0] as [number, number, number, number]),
        ...(stacked ? { borderColor: surface(mode), borderWidth: FILL_GAP } : {}),
      },
      emphasis: { itemStyle: { opacity: 0.85 } },
      label: labels
        ? {
            show: true,
            position: horizontal ? ("right" as const) : ("top" as const),
            color: c.secondary,
            fontSize: 11,
            formatter: (p: { value: number | null }) =>
              p.value == null ? "" : formatValue(p.value, { valueType: s.unit, context: "label" }),
          }
        : { show: false },
    }
  })

  return {
    ...base,
    tooltip: tooltipFor(mode, model, "item"),
    series,
  }
}

export function rankedBarOption(opts: Omit<BarOptions, "horizontal">): ChartOption {
  return barOption({ showDataLabels: true, ...opts, horizontal: true })
}
